import {
  PURGEABLE_COLLECTIONS,
  
  getPurgeConfig,
  getPurgeCutoffTimestamp
} from './purgeConfig'
import type {PurgeableCollection} from './purgeConfig';
import type { RxDatabase } from 'rxdb'

/**
 * Metadata about purge operations stored in localStorage
 */
interface PurgeMetadata {
  [collection: string]: {
    lastPurgeTime: number
    lastPurgeCount: number
    retentionDays: number
  }
}

export const PURGE_METADATA_KEY_PREFIX = 'rxdb_purge_metadata_'
// Legacy un-prefixed key from before per-workspace scoping — retained as a
// constant so cleanup code (performLogout, handlePendingResets) can remove it.
export const LEGACY_PURGE_METADATA_KEY = 'rxdb_purge_metadata'

const purgeMetadataKey = (workspaceId: string): string =>
  `${PURGE_METADATA_KEY_PREFIX}${workspaceId}`

/**
 * Service to manage automatic purging of soft-deleted documents
 */
export class PurgeService {
  private db: RxDatabase<any, any, any>
  private workspaceId: string
  private intervalId: NodeJS.Timeout | null = null
  private isRunning = false
  private config = getPurgeConfig()

  constructor(db: RxDatabase<any, any, any>, workspaceId: string) {
    this.db = db
    this.workspaceId = workspaceId
  }

  /**
   * Start the purge service
   * Runs an initial purge and then schedules periodic purges
   */
  async start(): Promise<void> {
    if (!this.config.enabled) {
      console.log('[PurgeService] Purge is disabled, not starting')
      return
    }

    if (this.isRunning) {
      console.log('[PurgeService] Already running')
      return
    }

    this.isRunning = true
    console.log(
      `[PurgeService] Starting with ${this.config.retentionDays} day retention`,
    )

    // Run initial purge
    await this.runPurge()

    // Schedule periodic purges
    this.intervalId = setInterval(() => {
      this.runPurge().catch((error) => {
        console.error('[PurgeService] Scheduled purge failed:', error)
      })
    }, this.config.runIntervalMs)

    console.log(
      `[PurgeService] Scheduled to run every ${this.config.runIntervalMs / 1000 / 60} minutes`,
    )
  }

  /**
   * Stop the purge service
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
    this.isRunning = false
    console.log('[PurgeService] Stopped')
  }

  /**
   * Run purge across all collections
   */
  private async runPurge(): Promise<void> {
    const cutoffTimestamp = getPurgeCutoffTimestamp(this.config.retentionDays)
    const cutoffDate = new Date(cutoffTimestamp).toISOString()

    console.log(
      `[PurgeService] Running purge for documents deleted before ${cutoffDate}`,
    )

    let totalPurged = 0
    const results: Record<string, number> = {}

    for (const collectionName of PURGEABLE_COLLECTIONS) {
      try {
        const count = await this.purgeCollection(
          collectionName,
          cutoffTimestamp,
        )
        results[collectionName] = count
        totalPurged += count
      } catch (error) {
        console.error(
          `[PurgeService] Error purging collection ${collectionName}:`,
          error,
        )
        results[collectionName] = 0
      }
    }

    // Update metadata
    this.updatePurgeMetadata(results)

    console.log(
      `[PurgeService] Purge complete. Total documents purged: ${totalPurged}`,
      results,
    )
  }

  /**
   * Purge soft-deleted documents from a specific collection
   */
  private async purgeCollection(
    collectionName: PurgeableCollection,
    cutoffTimestamp: number,
  ): Promise<number> {
    const collection = this.db[collectionName]

    if (!collection) {
      console.warn(
        `[PurgeService] Collection ${collectionName} not found, skipping`,
      )
      return 0
    }

    // Query for soft-deleted documents older than cutoff
    const query = collection.find({
      selector: {
        isDeleted: true,
        deletedOn: {
          $lt: cutoffTimestamp,
          $ne: null,
        },
      },
    })

    const docsToRemove = await query.exec()

    if (docsToRemove.length === 0) {
      return 0
    }

    console.log(
      `[PurgeService] Purging ${docsToRemove.length} documents from ${collectionName}`,
    )

    // Permanently remove documents by their IDs
    await collection.bulkRemove(docsToRemove.map((doc: any) => doc.id))

    return docsToRemove.length
  }

  /**
   * Get purge cutoff timestamp for filtering in pull handlers
   * This is used by replication handlers to filter out old deleted documents
   */
  static getPurgeCutoffForCollection(): number {
    const config = getPurgeConfig()
    return getPurgeCutoffTimestamp(config.retentionDays)
  }

  /**
   * Update purge metadata in localStorage
   */
  private updatePurgeMetadata(results: Record<string, number>): void {
    try {
      const metadata = this.getPurgeMetadata()

      for (const [collection, count] of Object.entries(results)) {
        metadata[collection] = {
          lastPurgeTime: Date.now(),
          lastPurgeCount: count,
          retentionDays: this.config.retentionDays,
        }
      }

      localStorage.setItem(
        purgeMetadataKey(this.workspaceId),
        JSON.stringify(metadata),
      )
    } catch (error) {
      console.error('[PurgeService] Error updating purge metadata:', error)
    }
  }

  /**
   * Get purge metadata from localStorage
   */
  private getPurgeMetadata(): PurgeMetadata {
    try {
      const data = localStorage.getItem(purgeMetadataKey(this.workspaceId))
      return data ? JSON.parse(data) : {}
    } catch (error) {
      console.error('[PurgeService] Error reading purge metadata:', error)
      return {}
    }
  }

  /**
   * Get purge statistics for monitoring/debugging
   */
  getStats(): PurgeMetadata {
    return this.getPurgeMetadata()
  }

  /**
   * Manually trigger a purge (for testing or admin purposes)
   */
  async triggerPurge(): Promise<void> {
    console.log('[PurgeService] Manual purge triggered')
    await this.runPurge()
  }
}
